import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthPage, type PublicAuthMode } from '../../components/public';
import { PUBLIC_AUTH_CONFIG } from '../../config/publicAuth';
import {
  resolvePublicFinancialAidEntryReturnTo,
  resolvePublicFinancialAidEntryState,
  type PublicFinancialAidEntryState,
} from '../../financial-aid/publicFinancialAidEntry';
import {
  getPublicApplySettings,
  type PublicApplySettings,
} from '../../settings/publicSettingsClient';
import { activateParentFinancialAidEntryFocus } from '../../financial-aid/parentFinancialAidStorage';
import styles from './PublicFinancialAidEntryRoute.module.css';

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; entry: PublicFinancialAidEntryState }
  | { status: 'error'; message: string };

const normalizeText = (value: unknown): string | null => {
  const text = String(value ?? '').trim();
  return text.length > 0 ? text : null;
};

const formatDateLabel = (value: string | null): string | null => {
  if (!value) return null;
  const parsed = Date.parse(`${value}T00:00:00Z`);
  if (!Number.isFinite(parsed)) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(parsed));
};

const resolveClosedMessage = (entry: PublicFinancialAidEntryState): string => {
  if (entry.closedReason === 'before_open') {
    const openDateLabel = formatDateLabel(entry.openDate);
    return openDateLabel
      ? `${entry.schoolName} financial aid applications for ${entry.schoolYear ?? 'the upcoming school year'} open on ${openDateLabel}.`
      : `${entry.schoolName} financial aid applications are not open yet.`;
  }
  if (entry.closedReason === 'after_cutoff') {
    const cutoffLabel = formatDateLabel(entry.hardCutoffDeadline);
    return cutoffLabel
      ? `The financial aid application deadline for ${entry.schoolYear ?? 'this school year'} passed on ${cutoffLabel}.`
      : `The financial aid application deadline for ${entry.schoolYear ?? 'this school year'} has passed.`;
  }
  return `${entry.schoolName} financial aid applications are not currently open.`;
};

const PublicFinancialAidClosedPage = ({ entry }: { entry: PublicFinancialAidEntryState }) => (
  <section className={styles.root} data-testid="parent-financial-aid-public-closed">
    <div className={styles.shell}>
      <div className={styles.panel}>
        <div className={styles.brand}>
          <div className={styles.logo} aria-hidden="true">
            {entry.logoUrl ? (
              <img
                className={styles.logoImage}
                src={entry.logoUrl}
                alt=""
              />
            ) : (
              entry.schoolShortName
            )}
          </div>
          <div>
            <p className={styles.schoolName}>{entry.schoolName}</p>
            <p className={styles.eyebrow}>Financial Aid</p>
          </div>
        </div>

        <h1 className={styles.title}>Applications are not currently open for {entry.schoolName}</h1>
        <p className={styles.body}>{resolveClosedMessage(entry)}</p>
        <p className={styles.body}>
          For questions about timing or a submitted application, contact the business office.
        </p>

        <div className={styles.contact} data-testid="parent-financial-aid-public-contact">
          {entry.contactEmail ? (
            <a className={styles.contactLink} href={`mailto:${entry.contactEmail}`}>
              {entry.contactEmail}
            </a>
          ) : null}
          {entry.contactPhone ? (
            <a className={styles.contactLink} href={`tel:${entry.contactPhone.replace(/[^\d+]/g, '')}`}>
              {entry.contactPhone}
            </a>
          ) : null}
          {!entry.contactEmail && !entry.contactPhone ? (
            <span className={styles.contactText}>Contact the school office for details.</span>
          ) : null}
        </div>
      </div>
    </div>
  </section>
);

const PublicFinancialAidEntryLoading = () => (
  <section className={styles.root} data-testid="parent-financial-aid-public-loading">
    <div className={styles.shell}>
      <div className={styles.panel}>
        <p className={styles.schoolName}>Loading financial aid access</p>
        <p className={`${styles.body} ${styles.muted}`}>Checking the current application cycle.</p>
      </div>
    </div>
  </section>
);

const PublicFinancialAidEntryError = ({ message }: { message: string }) => (
  <section className={styles.root} data-testid="parent-financial-aid-public-error">
    <div className={styles.shell}>
      <div className={styles.panel}>
        <h1 className={styles.title}>Unable to load financial aid access</h1>
        <p className={styles.body}>{message}</p>
      </div>
    </div>
  </section>
);

export const PublicFinancialAidEntryRoute = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  // General application links reach families without accounts, so the entry
  // defaults to account creation; existing families switch to sign in.
  const mode: PublicAuthMode = params.get('mode') === 'signin' ? 'signin' : 'signup';
  const initialEmail = normalizeText(params.get('email'));
  const inviteSchoolId = normalizeText(params.get('schoolId'));
  const requestedAcademicYear = normalizeText(params.get('academicYear'));
  const hasInviteToken = Boolean(normalizeText(params.get('invite')));
  const returnTo = resolvePublicFinancialAidEntryReturnTo(location.pathname, location.search);
  const googleProvisioningIntent = useMemo(() => {
    return {
      email: initialEmail,
      requestedPersona: 'parent' as const,
      requestedAccountType: 'parent' as const,
      schoolId: inviteSchoolId,
      financialAidInvite: hasInviteToken,
    };
  }, [hasInviteToken, initialEmail, inviteSchoolId]);

  useEffect(() => {
    let cancelled = false;
    setLoadState({ status: 'loading' });

    void getPublicApplySettings()
      .then((settings: PublicApplySettings) => {
        if (cancelled) return;
        setLoadState({
          status: 'ready',
          entry: resolvePublicFinancialAidEntryState(settings, Date.now(), requestedAcademicYear),
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const message = error instanceof Error && error.message.trim().length > 0
          ? error.message
          : 'Public financial aid settings are unavailable.';
        setLoadState({ status: 'error', message });
      });

    return () => {
      cancelled = true;
    };
  }, [requestedAcademicYear]);

  useEffect(() => {
    if (hasInviteToken) {
      activateParentFinancialAidEntryFocus();
      return;
    }
    if (loadState.status === 'ready' && loadState.entry.status === 'open') {
      activateParentFinancialAidEntryFocus();
    }
  }, [hasInviteToken, loadState]);

  const handleModeChange = (nextMode: PublicAuthMode) => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.set('mode', nextMode);
    navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true });
  };

  const handleAuthSuccess = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('ystnj-auth-session-updated'));
      window.location.assign(returnTo);
    }
  };

  if (loadState.status === 'loading') {
    return <PublicFinancialAidEntryLoading />;
  }

  if (loadState.status === 'error' && !hasInviteToken) {
    return <PublicFinancialAidEntryError message={loadState.message} />;
  }

  const closedEntryMatchesRequestedYear = Boolean(
    loadState.status === 'ready'
    && requestedAcademicYear
    && loadState.entry.schoolYear === requestedAcademicYear,
  );
  const shouldShowClosedCyclePage = Boolean(
    loadState.status === 'ready'
    && loadState.entry.status === 'closed'
    && (
      !hasInviteToken
      || (
        // Staff-created invite links do not override a configured closed
        // Financial Aid cycle. Token-only or unknown-year invites continue to
        // auth/backend resolution because this public settings payload cannot
        // safely identify the invite's application year.
        closedEntryMatchesRequestedYear
        && loadState.entry.closedReason !== 'not_configured'
      )
    ),
  );

  if (shouldShowClosedCyclePage && loadState.status === 'ready') {
    return <PublicFinancialAidClosedPage entry={loadState.entry} />;
  }

  const entry = loadState.status === 'ready' ? loadState.entry : null;

  return (
    <AuthPage
      mode={mode}
      onModeChange={handleModeChange}
      onAuthSuccess={handleAuthSuccess}
      enableGoogle={PUBLIC_AUTH_CONFIG.googleSocialEnabled}
      initialAccountType="parent"
      initialEmail={initialEmail}
      inviteSchoolId={inviteSchoolId}
      hideHeroPane={true}
      schoolName={entry?.schoolName ?? null}
      schoolLogoUrl={entry?.logoUrl ?? null}
      googleProvisioningIntent={googleProvisioningIntent}
      oauthRedirectState={{
        returnTo,
        defaultModule: null,
        mode,
        schoolId: inviteSchoolId,
      }}
    />
  );
};
